import { useApolloClient } from "@apollo/client";
import ContactsIcon from "@mui/icons-material/AssignmentInd";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Link, useParams } from "react-router-dom";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { OrganisationAvatar } from "../../features/organisations/OrganisationAvatar.js";
import { useSupplierProfileQuery } from "./SupplierProfile.generated.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export const SupplierProfile = ({
  submenuItems,
  children,
}: React.PropsWithChildren<Props>) => {
  const { supplierId } = useParams();
  if (!supplierId) throw new Error("Supplier id is missing");

  const { t } = useTranslate(["CrmOrganisations"]);

  const client = useApolloClient();
  const query = useSupplierProfileQuery({
    client,
    variables: {
      supplierId,
    },
  });

  const result = query.data?.pimSupplier;
  const supplier = result?.supplier;
  const crmCompany = supplier?.linkedCrmCompany;

  return (
    <Page title={supplier?.title} submenuItems={submenuItems}>
      <PageContainer>
        {supplier && (
          <Stack direction={"column"} spacing={2}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-start"
              alignItems={"stretch"}
            >
              <OrganisationAvatar organisationAvatar={supplier} size="xl" />
              <Stack direction="row" spacing={0.5} flex={1}>
                <Typography variant="h2" alignSelf={"center"}>
                  {supplier.title}
                </Typography>
              </Stack>
              <Box alignSelf={"flex-start"}>
                {crmCompany?.id && (
                  <RestrictedByOrganisationPermissionWithDebug permission="READ_CRM">
                    <Tooltip
                      title={t("Open in CRM", { ns: "CrmOrganisations" })}
                    >
                      <IconButton
                        color="primary"
                        component={Link}
                        to={`/crm/organisations/${crmCompany.id}`}
                      >
                        <ContactsIcon />
                      </IconButton>
                    </Tooltip>
                  </RestrictedByOrganisationPermissionWithDebug>
                )}
              </Box>
            </Stack>
            <Box>{children}</Box>
          </Stack>
        )}
      </PageContainer>
    </Page>
  );
};
